import { calculateNativeGrossValue, validateNativeQuantity } from '@einhaus/project314-shared';
import { formatNativeGrossValueEquation, formatQuantity } from '@/utils/nativeQuantity';
import React from 'react';
import Alert from '@/components/UI/Alert';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import type { UserPortfolioPosition } from '@einhaus/project314-types';
import type { ClosingTradeType, PortfolioWithAccountingFields, TradeConfirmation } from './types';
import { formatPositionSide } from '@/utils/tradeSemantics';
import {
    formatCurrency,
    getClosingTypeOptions,
    getPositionMarketValue,
    getPositionPremiumReceived,
    getPositionQuantity,
    getPositionQuantityLabel,
    getPositionTotalPnl,
    isOpenShortOptionPosition,
    isShortExposurePosition,
    isShortStockPosition
} from './helpers';

interface TradeModalProps {
    isOpen: boolean;
    position: UserPortfolioPosition | null;
    trades: PortfolioWithAccountingFields['trades'];
    tradeConfirmation: TradeConfirmation | null;
    tradeType: ClosingTradeType;
    tradeQuantity: number | null;
    limitPrice: number | null;
    isLoading: boolean;
    error: string | null;
    isImpersonating: boolean;
    onTradeTypeChange: (tradeType: ClosingTradeType) => void;
    onTradeQuantityChange: (quantity: number | null) => void;
    onLimitPriceChange: (price: number | null) => void;
    onGetQuote: () => void;
    onExecuteTrade: () => void;
    onBack: () => void;
    onClose: () => void;
}

const TradeModal: React.FC<TradeModalProps> = ({
    isOpen,
    position,
    trades,
    tradeConfirmation,
    tradeType,
    tradeQuantity,
    limitPrice,
    isLoading,
    error,
    isImpersonating,
    onTradeTypeChange,
    onTradeQuantityChange,
    onLimitPriceChange,
    onGetQuote,
    onExecuteTrade,
    onBack,
    onClose
}) => {
    const isOption = position?.derivativeType === 'Option';
    let validQuantity = false;
    if (position && tradeQuantity !== null) {
        try {
            validateNativeQuantity(tradeQuantity, position);
            validQuantity = tradeQuantity <= position.quantity;
        } catch {
            // The editable quantity is not yet executable; keep the quote action disabled.
        }
    }
    const validPrice = limitPrice !== null && Number.isFinite(limitPrice) && limitPrice > 0;
    const estimatedGrossValue =
        position && tradeQuantity !== null && limitPrice !== null && validQuantity && validPrice
            ? calculateNativeGrossValue({
                  instrument: position,
                  quantity: tradeQuantity,
                  price: limitPrice,
                  contractMultiplier: position.contractMultiplier
              })
            : null;

    return (
        <Modal
            isOpen={isOpen}
            size="lg"
            title={`Close position: ${position ? formatPositionSide(position.direction) : 'Position'}`}
            onClose={onClose}
        >
            <div className="p-3">
                {error && <Alert variant="danger">{error}</Alert>}

                <div className="mb-3">
                    <h5 className="text-light">Position Details</h5>
                    {position && (
                        <div className="w-full">
                            {(() => {
                                const selectedTrade = trades?.find((trade) => trade.id === position.tradeId);
                                const selectedPositionMarketValue = getPositionMarketValue(position);
                                const selectedPositionPremiumReceived = getPositionPremiumReceived(position);
                                const shouldShowPremiumReceived = selectedPositionPremiumReceived !== null;
                                const selectedPositionQuantity = getPositionQuantity(position);
                                const selectedPositionQuantityLabel = getPositionQuantityLabel(position);
                                const selectedPositionIsShortExposure = isShortExposurePosition(position);

                                const selectedPositionMarketValueLabel = isOpenShortOptionPosition(position)
                                    ? 'Liability to Close'
                                    : isShortStockPosition(position)
                                      ? 'Liability to Cover'
                                      : 'Market Value';

                                const selectedPositionTotalPnl = getPositionTotalPnl({
                                    position: {
                                        derivativeType: position.derivativeType,
                                        direction: position.direction,
                                        openTransactionType: position.openTransactionType,
                                        currentPrice: position.currentPrice,
                                        currentValue: position.currentValue,
                                        quantity: position.quantity,
                                        contractMultiplier: position.contractMultiplier,
                                        soldSharesValue: position.soldSharesValue,
                                        startingValue: position.startingValue
                                    },
                                    marketValue: selectedPositionMarketValue,
                                    transactions: selectedTrade?.transactions ?? undefined
                                });

                                return (
                                    <table className="table-fixed w-full divide-y divide-zinc-800 text-white">
                                        <thead className="bg-[#131416]">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-[15%]">
                                                    {selectedPositionQuantityLabel}
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-[20%]">
                                                    {isOption ? 'Current Premium' : 'Current Price'}
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-[20%]">
                                                    {selectedPositionMarketValueLabel}
                                                </th>
                                                {shouldShowPremiumReceived && (
                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-[20%]">
                                                        Premium Received
                                                    </th>
                                                )}
                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-[20%]">
                                                    {isOption ? 'Entry Premium' : 'Entry Price'}
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-[25%]">
                                                    Total P&L
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-[#222222] divide-y divide-zinc-800">
                                            <tr className="hover:bg-zinc-800">
                                                <td className="px-4 py-3">{formatQuantity(selectedPositionQuantity)}</td>
                                                <td className="px-4 py-3">${position.currentPrice.toLocaleString('en-US')}</td>
                                                <td className="px-4 py-3">
                                                    {formatCurrency(
                                                        selectedPositionIsShortExposure
                                                            ? Math.abs(selectedPositionMarketValue)
                                                            : selectedPositionMarketValue
                                                    )}
                                                </td>
                                                {shouldShowPremiumReceived && (
                                                    <td className="px-4 py-3">{formatCurrency(selectedPositionPremiumReceived)}</td>
                                                )}
                                                <td className="px-4 py-3">${position.entryPrice.toLocaleString('en-US')}</td>
                                                <td
                                                    className={`px-4 py-3 ${
                                                        selectedPositionTotalPnl > 0
                                                            ? 'text-profit-green'
                                                            : selectedPositionTotalPnl < 0
                                                              ? 'text-red-500'
                                                              : 'text-white'
                                                    }`}
                                                >
                                                    {formatCurrency(selectedPositionTotalPnl)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </div>
                    )}
                </div>

                {!tradeConfirmation ? (
                    <div>
                        <div className="mb-3">
                            <label className="form-label text-light" htmlFor="tradeType">
                                Trade Type
                            </label>
                            <FormSelect
                                disabled={isImpersonating}
                                id="tradeType"
                                options={position ? getClosingTypeOptions(position) : []}
                                value={tradeType}
                                onChange={(e) => onTradeTypeChange(e.target.value as ClosingTradeType)}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label text-light" htmlFor="tradeQuantity">
                                {position ? getPositionQuantityLabel(position) : 'Quantity'} to Close
                            </label>
                            <FormNumberInput
                                integer={position?.derivativeType === 'Option'}
                                disabled={isImpersonating}
                                helpText={isImpersonating ? 'Support sessions can only close the full open position.' : undefined}
                                id="tradeQuantity"
                                max={position?.quantity}
                                min={position?.derivativeType === 'Option' ? 1 : 0}
                                value={tradeQuantity}
                                onValueChange={onTradeQuantityChange}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label text-light" htmlFor="tradeLimitPrice">
                                {isOption ? 'Limit Premium Quote' : 'Limit Price'}
                            </label>
                            <FormNumberInput
                                helpText={
                                    isOption ? 'The quoted option premium per underlying unit, before the contract multiplier.' : undefined
                                }
                                id="tradeLimitPrice"
                                min={0.01}
                                step={0.01}
                                value={limitPrice}
                                onValueChange={onLimitPriceChange}
                            />
                        </div>

                        <div className="mb-3">
                            <span className="form-label text-light">Estimated Gross Value</span>
                            <div className="form-control bg-[#1a1a1a] text-light">
                                {estimatedGrossValue === null ? '—' : `$${estimatedGrossValue.toLocaleString('en-US')}`}
                            </div>
                            {isOption && estimatedGrossValue !== null && position && tradeQuantity !== null && limitPrice !== null && (
                                <p className="mt-1 text-sm text-zinc-400">
                                    {formatNativeGrossValueEquation({
                                        instrument: position,
                                        quantity: tradeQuantity,
                                        quotedPrice: limitPrice,
                                        contractMultiplier: position.contractMultiplier
                                    })}
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end">
                            <Button className="mr-2" disabled={isLoading} variant="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button disabled={isLoading || !validQuantity || !validPrice} variant="primary" onClick={onGetQuote}>
                                {isLoading ? 'Loading...' : 'Get Quote'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h5 className="text-light mb-3">Trade Confirmation</h5>
                        <div className="mb-3 w-full">
                            <table className="table-fixed w-full divide-y divide-zinc-800 text-white">
                                <tbody className="bg-[#222222] divide-y divide-zinc-800">
                                    <tr className="hover:bg-zinc-800">
                                        <td className="px-4 py-2 w-1/2">
                                            {position ? getPositionQuantityLabel(position) : 'Quantity'} to Close
                                        </td>
                                        <td className="px-4 py-2 w-1/2">{formatQuantity(tradeConfirmation.quantity)}</td>
                                    </tr>
                                    <tr className="hover:bg-zinc-800">
                                        <td className="px-4 py-2">{isOption ? 'Limit Premium Quote' : 'Limit Price'}</td>
                                        <td className="px-4 py-2">${tradeConfirmation.currentPrice.toLocaleString('en-US')}</td>
                                    </tr>
                                    <tr className="hover:bg-zinc-800">
                                        <td className="px-4 py-2">Estimated Gross Value</td>
                                        <td className="px-4 py-2">
                                            ${tradeConfirmation.estimatedValue.toLocaleString('en-US')}
                                            {isOption && position && (
                                                <div className="mt-1 text-xs text-zinc-400">
                                                    {formatNativeGrossValueEquation({
                                                        instrument: position,
                                                        quantity: tradeConfirmation.quantity,
                                                        quotedPrice: tradeConfirmation.currentPrice,
                                                        contractMultiplier: position.contractMultiplier
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-zinc-800">
                                        <td className="px-4 py-2">Estimated Fees</td>
                                        <td className="px-4 py-2">${tradeConfirmation.commission.toLocaleString('en-US')}</td>
                                    </tr>
                                    <tr className="hover:bg-zinc-800">
                                        <td className="px-4 py-2 font-bold">Net Value</td>
                                        <td className="px-4 py-2 font-bold">
                                            ${tradeConfirmation.netValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end">
                            <Button className="mr-2" disabled={isLoading} variant="secondary" onClick={onBack}>
                                Back
                            </Button>
                            <Button disabled={isLoading} variant="primary" onClick={onExecuteTrade}>
                                {isLoading ? 'Loading...' : 'Execute Trade'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default TradeModal;
